import React, { useCallback, useEffect, useMemo, useState } from "react";
import Modal from "../../components/Atomos/Modals/Modal";
import Input from "../../components/Atomos/Inputs/Input";
import Dropdown from "../../components/Atomos/DropDown/Dropdown";
import TextArea from "../../components/Atomos/Inputs/TextArea";
import {
  AccountingModalError,
  AccountingModalFooter,
} from "./accountingModalUi";
import {
  applyManualBalanceAdjustment,
  BALANCE_REASON_CODES,
  resolvePerformedBy,
} from "../../utils/accountingOfficeBalance";
import type { AccountingOfficeDetail } from "../../utils/accountingOfficeDetail";
import { useAuth } from "../../store/AuthContext";
import { toastError, toastSuccess } from "../../utils/toast";

type AdjustDirection = "add" | "deduct";

type AdjustOfficeBalanceModalProps = {
  office: AccountingOfficeDetail;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
};

function parseAmount(value: string): number {
  const cleaned = value.replace(/[^0-9.]/g, "");
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
}

export default function AdjustOfficeBalanceModal({
  office,
  isOpen,
  onClose,
  onSaved,
}: AdjustOfficeBalanceModalProps): React.ReactElement {
  const { loggedPerson, tokenDecoded } = useAuth();
  const [direction, setDirection] = useState<AdjustDirection>("add");
  const [amount, setAmount] = useState("");
  const [reasonCode, setReasonCode] = useState("manual_entry");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setDirection("add");
    setAmount("");
    setReasonCode("manual_entry");
    setNotes("");
    setError("");
    setSaving(false);
  }, [isOpen]);

  const selectedReasonName = useMemo(() => {
    return (
      BALANCE_REASON_CODES.find((item) => item.id === reasonCode)?.name ??
      "Manual Entry / Correction"
    );
  }, [reasonCode]);

  const handleSave = useCallback(async () => {
    const parsedAmount = parseAmount(amount);
    if (!parsedAmount || parsedAmount <= 0) {
      setError("Enter a valid amount greater than zero.");
      return;
    }
    if (!reasonCode) {
      setError("Reason code is required.");
      return;
    }

    setSaving(true);
    setError("");

    try {
      const { performedById, performedByName } = resolvePerformedBy(
        loggedPerson,
        tokenDecoded,
      );

      await applyManualBalanceAdjustment({
        officeId: office.id,
        firmId: office.firm ?? null,
        direction,
        amount: parsedAmount,
        reasonCode,
        notes,
        performedById,
        performedByName,
      });

      toastSuccess("Balance adjustment saved.");
      onSaved();
      onClose();
    } catch (saveError) {
      const message =
        saveError instanceof Error
          ? saveError.message
          : "Unable to save balance adjustment. Please try again.";
      setError(message);
      toastError(message);
    } finally {
      setSaving(false);
    }
  }, [
    amount,
    direction,
    loggedPerson,
    notes,
    office,
    onClose,
    onSaved,
    reasonCode,
    tokenDecoded,
  ]);

  return (
    <Modal
      isModalOpen={isOpen}
      toggleModal={onClose}
      title="Adjust Balance"
      titleColor="text-primary font-decimal text-2xl font-medium leading-8"
      compact
      compactBodyScrolls={false}
      className="max-w-xl"
    >
      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-gray-font">
            Adjustment Type
          </span>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setDirection("add")}
              className={
                direction === "add"
                  ? "rounded border border-accent-Default-dark bg-accent-Default-light px-4 py-2 text-sm font-semibold text-accent-Default-dark"
                  : "rounded border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-font"
              }
            >
              Add Amount
            </button>
            <button
              type="button"
              onClick={() => setDirection("deduct")}
              className={
                direction === "deduct"
                  ? "rounded border border-primary bg-[#FFF0F0] px-4 py-2 text-sm font-semibold text-primary"
                  : "rounded border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-font"
              }
            >
              Deduct Amount
            </button>
          </div>
        </div>

        <Input
          title="Amount (USD)"
          type="text"
          value={amount}
          placeholder="$ 0.00"
          onChange={(event) => setAmount(event.target.value)}
          error={false}
        />

        <Dropdown
          title="Reason Code"
          data={[...BALANCE_REASON_CODES]}
          selectedObjst={selectedReasonName}
          getid
          disableSort
          seter={(selected: { id?: string } | null) => {
            setReasonCode(String(selected?.id ?? "manual_entry"));
          }}
          error={false}
        />

        <TextArea
          title="Additional Notes"
          value={notes}
          placeholder="Provide extra context here..."
          onChange={(event) => setNotes(event.target.value)}
          error={false}
          height="h-[120px]"
        />

        <AccountingModalError message={error} />

        <AccountingModalFooter
          onCancel={onClose}
          onSubmit={() => void handleSave()}
          submitText="Save Adjustment"
          saving={saving}
          submitColor="primary"
        />
      </div>
    </Modal>
  );
}
